import React from "react";
import { ExclamationOutlined } from "@ant-design/icons";
import { Button, Form, Input, Popover } from "antd";
import { useTranslation } from "react-i18next";

import { type Dish } from "@fitness-app/data-models/entities/Dish";
import { type Ingredient, type IngredientWithPortion } from "@fitness-app/data-models/entities/Ingredient";
import { type DishInMeal } from "@fitness-app/data-models/entities/MealsPlan";

import { analyticsService } from "~/store/initializeStore";
import { useReportDishIssue } from "../hooks/useReportDishIssue";

interface ErrorReportFormProps {
  dishOrIngredient: DishInMeal | Ingredient | Dish | IngredientWithPortion;
  onReport?: () => void;
}

type FormModel = {
  reason: string;
};

export const ErrorReportForm: React.FC<ErrorReportFormProps> = ({ dishOrIngredient, onReport }) => {
  const { t } = useTranslation("nutrition");
  const [form] = Form.useForm<FormModel>();
  const [open, setOpen] = React.useState(false);
  const { reportIssue, isReporting } = useReportDishIssue();

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      await reportIssue(
        dishOrIngredient.type === "dish" || dishOrIngredient.type !== "ingredient"
          ? {
              dishId:
                "parentId" in dishOrIngredient && dishOrIngredient.parentId
                  ? dishOrIngredient.parentId
                  : dishOrIngredient.id,
              reason: values.reason,
              ownerId: dishOrIngredient.createdBy,
            }
          : {
              ingredientId:
                "ingredientId" in dishOrIngredient && dishOrIngredient.ingredientId
                  ? dishOrIngredient.ingredientId
                  : dishOrIngredient.id,
              reason: values.reason,
              ownerId: dishOrIngredient.createdBy,
            },
      );
      form.resetFields();
      setOpen(false);
      analyticsService.track("report_dish_issue", {
        dishId: dishOrIngredient.id,
        reason: values.reason,
        ownerId: dishOrIngredient.createdBy,
      });
      onReport?.();
    } catch (error) {
      // Form validation failed
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setOpen(false);
  };

  if (dishOrIngredient.type === "ingredient") {
    return null;
  }

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      trigger="click"
      placement="left"
      title={t("reportError.title")}
      content={
        <Form<FormModel> form={form} layout="vertical" style={{ width: 300 }}>
          <Form.Item
            name="reason"
            rules={[{ required: true, message: t<string>("common:validationErrors.fieldIsRequired") }]}
          >
            <Input.TextArea rows={4} placeholder={t<string>("reportError.description")} maxLength={500} showCount />
          </Form.Item>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <Button onClick={handleCancel}>{t("reportError.button.cancel")}</Button>
            <Button type="primary" onClick={handleSubmit} loading={isReporting}>
              {t("reportError.button.report")}
            </Button>
          </div>
        </Form>
      }
    >
      <Button
        loading={isReporting}
        color="orange"
        title={t<string>("reportError.title")}
        variant="outlined"
        icon={<ExclamationOutlined />}
      />
    </Popover>
  );
};
